import { useEffect } from 'react';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import { LoggingOptions } from '@/hooks/useQueryWithLogging';
import { error as errorLog, info } from '@/utils/logging';

export function useMutationWithLogging<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  loggingOptions: LoggingOptions = {},
): UseMutationResult<TData, TError, TVariables, TContext> {
  const {
    logSuccess = true,
    logError = true,
    logSettled = false,
    context = {},
  } = loggingOptions;

  const mutationKey = options.mutationKey;
  const mutationKeyString = Array.isArray(mutationKey)
    ? mutationKey.join(':')
    : mutationKey
      ? String(mutationKey)
      : 'unknown-mutation';

  const result = useMutation(options);
  const { data, error, status } = result;

  // Use useEffect to log based on mutation state changes
  useEffect(() => {
    if (logSuccess && result.isSuccess && result.data !== undefined) {
      info(null, `Mutation succeeded: ${mutationKeyString}`, {
        mutationKey: mutationKey,
        dataLength: Array.isArray(data) ? data.length : undefined,
        hasData: !!data,
        ...context,
      });
    }

    if (logError && result.isError && result.error) {
      errorLog(result.error, `Mutation failed: ${mutationKeyString}`, {
        mutationKey: mutationKey,
        errorMessage: error instanceof Error ? error.message : String(error),
        ...context,
      });
    }

    if (logSettled && (result.isSuccess || result.isError)) {
      info(null, `Mutation settled: ${mutationKeyString}`, {
        mutationKey: mutationKey,
        hasData: !!data,
        hasError: !!error,
        status: status,
        ...context,
      });
    }
  }, [
    result.isSuccess,
    result.isError,
    result.data,
    result.error,
    result.status,
    mutationKeyString,
    logSuccess,
    logError,
    logSettled,
  ]);

  return result;
}
